import { mcpErrorMessage, type MCPApiError as ApiError } from "../errors";
import {
  useQueryClient,
} from "@tanstack/react-query";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import {
  agentToolsApi,
  type MCPConnectionDTO,
  type PaginatedMCPConnectionsResponse,
  type UpdateMCPConnectionPayload,
} from "../../../services/agent-tools-service";
import { mcpQueryKeys } from "../query-keys";
import type { MCPQueryScope } from "../query-keys";
import { canNotifyMCPMutation, useScopedMCPMutation } from "./useScopedMCPMutation";
import { mcpRequestOptions } from "./useMCPQueryScope";

type Options = {
  onSuccess?: (dto: MCPConnectionDTO) => void;
};

type Variables = {
  id: string;
  payload: UpdateMCPConnectionPayload;
};

type MutationContext = {
  previousDetail?: MCPConnectionDTO;
};

const writeConnectionToCaches = (
  queryClient: ReturnType<typeof useQueryClient>,
  scope: MCPQueryScope,
  id: string,
  next: MCPConnectionDTO,
) => {
  queryClient.setQueryData<MCPConnectionDTO>(mcpQueryKeys.detail(scope, id), next);
  queryClient.setQueriesData<PaginatedMCPConnectionsResponse>(
    { queryKey: mcpQueryKeys.lists(scope) },
    (old) => {
      if (!old?.items) return old;
      return {
        ...old,
        items: old.items.map((item) => (item.id === id ? next : item)),
      };
    },
  );
};

export const useUpdateMCPConnection = (options: Options = {}) => {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();

  return useScopedMCPMutation<MCPConnectionDTO, Variables, MutationContext, ApiError>({
    mutationFn: ({ id, payload }, scope) =>
      agentToolsApi.updateMCPConnection(id, payload, mcpRequestOptions(scope)),
    // Optimistically patch the cached connection so role/active toggles feel
    // instant. We snapshot the previous detail for rollback on error.
    onMutate: async ({ id, payload }, scope): Promise<MutationContext> => {
      if (!scope.enabled || !scope.active) return {};
      await queryClient.cancelQueries({ queryKey: mcpQueryKeys.detail(scope.key, id) });
      const previousDetail = queryClient.getQueryData<MCPConnectionDTO>(
        mcpQueryKeys.detail(scope.key, id),
      );
      if (previousDetail) {
        writeConnectionToCaches(queryClient, scope.key, id, {
          ...previousDetail,
          ...payload,
        } as MCPConnectionDTO);
      }
      return { previousDetail };
    },
    onSuccess: (updated, { id }, scope) => {
      // Reconcile with the authoritative server response.
      writeConnectionToCaches(queryClient, scope.key, id, updated);
      queryClient.invalidateQueries({ queryKey: mcpQueryKeys.lists(scope.key) });
      queryClient.invalidateQueries({
        queryKey: mcpQueryKeys.test(scope.key, id),
        exact: true,
      });
      if (canNotifyMCPMutation(scope)) options.onSuccess?.(updated);
    },
    onError: (error: ApiError, { id }, context, scope) => {
      // Roll back the optimistic patch.
      if (context?.previousDetail) {
        writeConnectionToCaches(queryClient, scope.key, id, context.previousDetail);
      }
      if (canNotifyMCPMutation(scope, error)) {
        toast.error(mcpErrorMessage(error, t("mcpPage.mutation.updateFailed"), i18n.resolvedLanguage ?? i18n.language));
      }
    },
  });
};
